import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import {
  QUESTION_TYPE_CHECKBOX,
  QUESTION_TYPE_RADIO,
  QUESTION_TYPE_SELECT,
} from "@/schemas/question";
import { Question } from "@/types/question";
import { useFormContext, useFormState } from "react-hook-form";
import { useTranslation } from "react-i18next";
import DependableQuestionSelect from "./components/DependableQuestionSelect";
import NoDependableQuestionsMessage from "./components/NoDependableQuestionsMessage";
import VariantList from "./components/VariantList";

const DEPENDABLE_QUESTION_TYPES = [
  QUESTION_TYPE_SELECT,
  QUESTION_TYPE_RADIO,
  QUESTION_TYPE_CHECKBOX,
];

interface DependentQuestionsProps {
  index: number;
}

export default function DependentQuestions({ index }: DependentQuestionsProps) {
  const { t } = useTranslation();
  const { getValues, watch } = useFormContext();
  const { isSubmitting, disabled } = useFormState();
  const hidden = watch(`questions.${index}.hidden`);
  const questions = getValues(`questions`);
  const question = getValues(`questions.${index}`);

  const isDependableQuestion = (question: Question) =>
    DEPENDABLE_QUESTION_TYPES.includes(question.type);

  const dependableQuestions = questions.filter(
    (q: Question) => isDependableQuestion(q) && q.id !== question.id,
  );

  const hasAnyDependableQuestion = dependableQuestions.length > 0;

  const indexOfReqField = questions.findIndex(
    (q: Question) => q.id == question.dependentOnQuestion,
  );

  const variants = getValues(`questions.${indexOfReqField}.cfg.variants`) || [];

  return (
    <Card className="bg-white p-3 mt-2">
      <CardHeader className="p-2">
        <FormField
          name={`questions.${index}.hidden`}
          render={({ field }) => (
            <FormItem className="flex items-center space-y-0">
              <FormControl>
                <Checkbox
                  className="mr-2"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  disabled={isSubmitting || disabled}
                />
              </FormControl>
              <FormLabel>{t("dependentQuestions.title")}</FormLabel>
            </FormItem>
          )}
        />
      </CardHeader>
      {hidden && (
        <CardContent className="p-2">
          {hasAnyDependableQuestion && (
            <>
              <FormField
                name={`questions.${index}.dependentOnQuestion`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("dependentQuestions.selectQuestion")}
                    </FormLabel>
                    <DependableQuestionSelect
                      options={dependableQuestions}
                      {...field}
                    />
                  </FormItem>
                )}
              />

              <div className="mt-2">
                <FormLabel>
                  {t("dependentQuestions.activationVariants")}
                </FormLabel>
                <VariantList variants={variants} index={index} />
              </div>
            </>
          )}
          {hasAnyDependableQuestion === false && (
            <NoDependableQuestionsMessage
              supportedTypes={DEPENDABLE_QUESTION_TYPES}
            />
          )}
        </CardContent>
      )}
    </Card>
  );
}
